import React, { Ref, useCallback, useRef } from 'react'
import { useDrop } from 'react-dnd'

import { Nullable } from '@types'

import { colors } from '@theme/variables'

import { DropArea } from './index.styles'

export interface DroppableTabAreaProps {
  label: string
  acceptItemType: string
  tabId: number
  activeTabId: number
  handleDrop?: (tabIdFrom: number, tadIdTo: number, droppedItem: unknown, dropAnchor: Nullable<HTMLElement>) => void
}

const DroppableTabArea = ({ label, acceptItemType, activeTabId, tabId, handleDrop }: DroppableTabAreaProps) => {
  const dropAreaRef = useRef<HTMLElement | null>(null)

  const handleDropCallback = useCallback(
    (item: unknown) => handleDrop && handleDrop(activeTabId, tabId, item, dropAreaRef.current),
    // eslint-disable-next-line
    [activeTabId, tabId],
  )
  const [collectedProps, drop] = useDrop(
    () => ({
      accept: acceptItemType,
      drop: handleDropCallback,
      collect: monitor => ({
        isOver: monitor.isOver(),
        canDrop: monitor.canDrop(),
      }),
    }),
    [handleDrop, activeTabId, tabId],
  )

  const refSetter = useCallback((el: HTMLDivElement) => {
    dropAreaRef.current = el || dropAreaRef.current
    return drop(el)
    // eslint-disable-next-line
  }, [])

  const styleProps = {
    borderWidth: '1px',
    borderStyle: 'dashed',
    borderColor:
      (collectedProps.canDrop && !collectedProps.isOver && colors.activeAreaBorder) ||
      (collectedProps.isOver && colors.primaryDark) ||
      'transparent',
  }

  return (
    <DropArea data-testid={`drop-area-${tabId}`} style={styleProps} ref={refSetter as unknown as Ref<HTMLDivElement>}>
      {label}
    </DropArea>
  )
}

export default DroppableTabArea
